/*
 https://wavesaudio.atlassian.net/browse/DSWEB-1185

 There are 2 campaign types
 ----------------------------
 1. TotalTiers, CampaignTypeID = 3, if user purchases > given amount -> receives free plugins

 2. MultiPlugin, CampaignTypeID = 5, if user purchases > given number of PLUGINS -> receives free plugins

 There are 2 types of controls:
 ------------------------------
 1. Green confirmation message in the first step below the 'Order Total'
 2. Popup that appears when user moves from step 1 to step 2
    Each campaign type has its own popup (with different UI)
*/

import { ICartData } from '../interfaces';
import { FreePluginEnum } from '../enums/free-plugins-enum';
import { ProductDepartmentEnum } from '../../../products/enums/product-department-enum';
import { getSelectedCartItems } from '../data/data-provider';
import { round2DecimalPlaces } from '../../../../domain/math-provider';
import { checkIfProgessBarEnabled } from '../ui/free-plugin/free-plugin-progress-bar';

/**
 * get plugins number including qty
 */
const getPluginsNumber = (cartData: ICartData) => {
    return cartData.cartItems.reduce(
        (count, cartItem) => (cartItem.skuDepartmentID === ProductDepartmentEnum.Plugins) && cartItem.isActive ?
        count + cartItem.skuUnits :
        count,
    0);
};

/**
 * Check if campaign data saved in the store is valid.
 * It should be a non-empty array.
 */
export const isCampaignDataValid = (cartData: ICartData) => {
    return cartData && cartData.efsData && Array.isArray(cartData.efsData) && cartData.efsData.length > 0;
};

/**
 * if campaign type = TotalTiers, CampaignTypeID = 3 - used for the green confirmation
 * check if user eligible to get the free plugins
 */
export const isTotalTiersEligible = (cartData: ICartData) => {
    if(!cartData.efsData || cartData.efsData.length <= 0) return false;

    const efsData = [...cartData.efsData];
    efsData.sort((item1, item2) => {
        return item1.cap - item2.cap;
    });

    const cap = Number(efsData[0].cap) || 0;
    const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

    if(subtotalWithoutDiscount < cap) return false;

    const activeCartItems = getSelectedCartItems(cartData);
    
    for (let i = 0; i < activeCartItems.length; i++) {
        const cartItem = activeCartItems[i];

        if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Software &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.Courses &&
            cartItem.skuDepartmentID !== ProductDepartmentEnum.CoursesWithSoftware) {
            return false;
        }
    }

    return true;
};

/**
 * If campaign type = MultiPlugin, CampaignTypeID = 5,
 * check if user eligible to get the free plugins.
 */
const isMultiPluginEligible = (cartData: ICartData) => {
    if(!cartData.efsData || cartData.efsData.length <= 0) return false;

    const minNumberOfPurchasedPlugins = Number(cartData.efsData[0].minNumberOfPurchasedPlugins) || 0;
    const pluginsCount = getPluginsNumber(cartData);
    return pluginsCount >= minNumberOfPurchasedPlugins;
};

export interface IConfirmationMsgVisibleResult {
    progressBarEnabled: boolean;
    addMoreEnabled: boolean;
    showTCLinkSeparately?: boolean;
}

export enum ConfirmationMsgVisibleReferer {
    Cart = 'cart',
    TopMinicart = 'top-mini-cart',
    AddToCartPopup = 'add-to-cart-popup',
}

/**
 * Check if green confirmation message in the first step below the 'Order Total' is visible.
 */
export const isConfirmationMsgVisible = (
    cartData: ICartData | null | undefined,
    referrer: ConfirmationMsgVisibleReferer,
    cartStep = 1,
) : IConfirmationMsgVisibleResult => {

    if(!cartData) {
        return {
            addMoreEnabled: false,
            progressBarEnabled: false,
        }
    }

    const campaignID = Number(cartData.efsCampaignTypeID) || 0;

    if(campaignID === FreePluginEnum.TotalTiers) {
        return {
            addMoreEnabled: false,
            progressBarEnabled: false,
        }
    }

    const progressBarEnabled = (cartStep === 3)? false : checkIfProgessBarEnabled(
        cartData,
        // referrer === ConfirmationMsgVisibleReferer.Cart,
    );

    // the campaign data should exist and be non-empty
    if(!isCampaignDataValid(cartData)) {
        return {
            addMoreEnabled: false,
            progressBarEnabled,
        }
    }


    if(campaignID === 0) {
        return {
            addMoreEnabled: false,
            progressBarEnabled,
        }
    }

    switch (campaignID){

        // TotalTiers, CampaignTypeID = 3, if user purchases > given amount -> receives free plugins
        case FreePluginEnum.TotalTiers: {
            return {
                addMoreEnabled: isTotalTiersEligible(cartData),
                progressBarEnabled,
            };
        }

        // MultiPlugin, CampaignTypeID = 5, if user purchases > given number of products -> receives free plugins
        case FreePluginEnum.MultiPlugin: {
            return {
                addMoreEnabled: isMultiPluginEligible(cartData),
                progressBarEnabled
            };
        }
    }

    return {
        addMoreEnabled: false,
        progressBarEnabled,
    }
};

const getSelectedTotalTierIndex = (cartData: ICartData) => {

    const campaignData = cartData.efsData;
    const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);

    for(let i=0; i<campaignData.length; i++){
        const item = campaignData[i];

        if(i === campaignData.length - 1 && subtotalWithoutDiscount >= item.cap) return i;
        if(subtotalWithoutDiscount >= item.cap && subtotalWithoutDiscount < item.upperCap) return i;
    }

    return -1;
};

export const getNumberOfFreePlugins = (cartData: ICartData) => {
    if(!cartData?.efsData?.length) return 0;

    const campaignID = Number(cartData.efsCampaignTypeID) || 0;

    if(campaignID === FreePluginEnum.TotalTiers){
        const selectedTierIndex = getSelectedTotalTierIndex(cartData);
        if(selectedTierIndex !== -1){
            return cartData.efsData[selectedTierIndex].numberOfFreePlugins;
        }
    }

    return cartData.efsData[0].numberOfFreePlugins;
};

// ---------------------------------------

// https://wavesaudio.atlassian.net/browse/DSWEB-5070

export interface IFreePluginProgressBar {
    enabled: boolean;
    min?: number;
    max?: number;
    value?: number;
    ariaText?: string;
    percent?: number;
    label?: {
        type: 'amount' | 'count';
        missingAmount: number;
        missingCount: number;
        receiveCount: number;
    }
}

export const initFreePluginProgressBar = (cartData: ICartData) : IFreePluginProgressBar => {

    // the campaign data should exist and be non-empty
    if(!isCampaignDataValid(cartData)) {
        return {
            enabled: false,
        };
    }

    const campaignID = Number(cartData.efsCampaignTypeID) || 0;
    if(campaignID === 0) {
        return {
            enabled: false,
        };
    }

    const efsData = [...cartData.efsData];
    efsData.sort((item1, item2) => {
        return item1.cap - item2.cap;
    });

    switch (campaignID){
        // TotalTiers, CampaignTypeID = 3, if user purchases > given amount -> receives free plugins
        case FreePluginEnum.TotalTiers: {

            const subtotalWithoutDiscount = Math.max(0, cartData.priceData.subTotalPrice - cartData.priceData.totalDiscountPrice);
            const lastCap = Number(efsData.at(-1)?.cap) || 0;

            if(subtotalWithoutDiscount >= lastCap) {
                // User already added amount big enough
                return {
                    enabled: false,
                };
            }

            const activeCartItems = getSelectedCartItems(cartData);
            for (let i = 0; i < activeCartItems.length; i++) {
                const cartItem = activeCartItems[i];

                if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
                    cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins &&
                    cartItem.skuDepartmentID !== ProductDepartmentEnum.Software &&
                    cartItem.skuDepartmentID !== ProductDepartmentEnum.Courses &&
                    cartItem.skuDepartmentID !== ProductDepartmentEnum.CoursesWithSoftware) {

                    // If other products in cart ---> don't show the progress bar
                    return {
                        enabled: false,
                    };
                }
            }

            for(let i=0; i<efsData.length; i++){
                const efsDataItem = efsData[i];
                if(efsDataItem.cap <= 0) continue;

                if(subtotalWithoutDiscount < efsDataItem.cap) {

                    const missingAmount = efsDataItem.cap - subtotalWithoutDiscount;
                    const percent = subtotalWithoutDiscount * 100 / efsDataItem.cap;
                    const receiveCount = efsDataItem.numberOfFreePlugins;

                    const missingAmountRounded = round2DecimalPlaces(missingAmount);
                    /*if(missingAmountRounded < 1) {
                        return {
                            enabled: false,
                        };
                    }*/

                    const percentRounded = round2DecimalPlaces(percent);

                    return  {
                        enabled: true,
                        min: 0,
                        max: efsDataItem.cap,
                        value: missingAmountRounded,
                        ariaText: `Add $${ missingAmountRounded } to get ${ receiveCount } FREE plugin${ receiveCount === 1 ? '' : 's' }`,
                        percent: percentRounded,
                        label: {
                            type: 'amount',
                            missingAmount: missingAmountRounded,
                            missingCount: 0,
                            receiveCount,
                        }
                    }
                }
            }
            break;
        }


        // MultiPlugin, CampaignTypeID = 5, if user purchases > given number of products -> receives free plugins
        case FreePluginEnum.MultiPlugin: {
            const pluginsCount = getPluginsNumber(cartData);
            const lastItemPluginsCount = Number(efsData.at(-1)?.minNumberOfPurchasedPlugins) || 0;

            if(pluginsCount >= lastItemPluginsCount) {
                // User already added enough plugins
                return {
                    enabled: false,
                };
            }

            const activeCartItems = getSelectedCartItems(cartData);
            for (let i = 0; i < activeCartItems.length; i++) {
                const cartItem = activeCartItems[i];

                if (cartItem.skuDepartmentID !== ProductDepartmentEnum.Bundles &&
                    cartItem.skuDepartmentID !== ProductDepartmentEnum.Plugins) {
                    // If other products in cart ---> don't show the progress bar
                    return {
                        enabled: false,
                    };
                }
            }

            for(let i=0; i<efsData.length; i++){
                const efsDataItem = efsData[i];
                if(efsDataItem.minNumberOfPurchasedPlugins <= 0) continue;

                if(pluginsCount < efsDataItem.minNumberOfPurchasedPlugins) {

                    const missingCount = efsDataItem.minNumberOfPurchasedPlugins - pluginsCount;
                    const percent = missingCount * 100 / efsDataItem.minNumberOfPurchasedPlugins;
                    const receiveCount = efsDataItem.numberOfFreePlugins;

                    const missingCountRounded = round2DecimalPlaces(missingCount);
                    const percentRounded = round2DecimalPlaces(percent);

                    return  {
                        enabled: true,
                        min: 0,
                        max: efsDataItem.minNumberOfPurchasedPlugins,
                        value: missingCountRounded,
                        ariaText: `Add ${ missingCountRounded } more plugin${ missingCountRounded === 1 ? '' : 's' }, get ${ receiveCount } FREE plugin${ receiveCount === 1 ? '' : 's' }`,
                        percent: percentRounded,
                        label: {
                            type: 'count',
                            missingAmount: 0,
                            missingCount: missingCountRounded,
                            receiveCount,
                        }
                    }
                }
            }
            break;
        }
    }

    return {
        enabled: false,
    };
};
